import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { orgKey } from "@/hooks/useAuth";
import { env } from "@/utils/env";
import { useQuery } from "@tanstack/react-query";

interface QuestSubmission {
  tasks: Record<
    string,
    {
      completed: boolean;
      updatedAt: string;
      imageUrl?: string;
      latitude?: number;
      longitude?: number;
      distance?: number;
    }
  >;
}

interface QuestTask {
  id: string;
  title: string;
  completed: boolean;
  taskType: string;
  hasPhotoUpload?: boolean;
}

interface QuestWeek {
  id: string;
  title: string;
  tasks: QuestTask[];
}

interface Quest {
  id: string;
  title: string;
  description: string;
  weeks: QuestWeek[];
}

interface ResidentDetailProps {
  resident: {
    data: {
      id: string;
      email: string;
      fullName?: string;
      address?: string;
      city?: string;
      state?: string;
      zip?: string;
      phoneNumber?: string;
      createdAt: string;
    };
  };
}

export function ResidentDetail({ resident }: ResidentDetailProps) {
  const residentData = resident?.data;
  const questId = "wh2quccw2gr62xl2wmybjzyw"; // Vineyard scavenger hunt quest ID

  // Fetch quest details
  const { data: questData } = useQuery({
    queryKey: ["quest", questId],
    queryFn: async () => {
      const response = await fetch(`${env.VITE_HONO_URL}/quests/${questId}`, {
        headers: {
          "x-org-id": localStorage.getItem(orgKey) || "",
          "x-audience-id": residentData.id,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch quest");
      const data = await response.json();
      return data.data.quest as Quest;
    },
    enabled: !!residentData,
  });

  // Fetch resident's quest progress
  const { data: progressData, isLoading: progressLoading } = useQuery({
    queryKey: ["questProgress", questId, residentData?.id],
    queryFn: async () => {
      const response = await fetch(
        `${env.VITE_HONO_URL}/quests/${questId}/progress`,
        {
          headers: {
            "x-org-id": localStorage.getItem(orgKey) || "",
            "x-audience-id": residentData.id,
          },
        },
      );
      if (!response.ok) throw new Error("Failed to fetch quest progress");
      const data = await response.json();
      return data.data.questSubmission.json as QuestSubmission;
    },
    enabled: !!residentData,
  });

  if (!residentData) {
    return <div>Resident not found</div>;
  }

  const getCompletedTasksCount = () => {
    if (!progressData) return 0;
    return Object.keys(progressData.tasks).length;
  };

  const getTotalTasksCount = () => {
    if (!questData) return 0;
    return questData.weeks.reduce((sum, week) => sum + week.tasks.length, 0);
  };

  return (
    <Tabs defaultValue="details">
      <TabsList className="mb-4">
        <TabsTrigger value="details">Personal Info</TabsTrigger>
        <TabsTrigger value="quests">Quest Progress</TabsTrigger>
      </TabsList>

      <TabsContent value="details">
        <Card>
          <CardHeader>
            <CardTitle>{residentData.fullName || "Unnamed Resident"}</CardTitle>
            <CardDescription>Resident Details</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium">Email</h3>
                  <p className="text-sm text-muted-foreground">
                    {residentData.email}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium">Phone</h3>
                  <p className="text-sm text-muted-foreground">
                    {residentData.phoneNumber || "Not provided"}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium">Address</h3>
                  <p className="text-sm text-muted-foreground">
                    {residentData.address || "Not provided"}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium">City</h3>
                  <p className="text-sm text-muted-foreground">
                    {residentData.city || "Not provided"}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium">State</h3>
                  <p className="text-sm text-muted-foreground">
                    {residentData.state || "Not provided"}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium">ZIP</h3>
                  <p className="text-sm text-muted-foreground">
                    {residentData.zip || "Not provided"}
                  </p>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium">Joined</h3>
                <p className="text-sm text-muted-foreground">
                  {new Date(residentData.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="quests">
        <Card>
          <CardHeader>
            <CardTitle>Scavenger Hunt Progress</CardTitle>
            <CardDescription>
              {progressLoading
                ? "Loading..."
                : progressData
                  ? `Completed ${getCompletedTasksCount()} of ${getTotalTasksCount()} tasks`
                  : "No quest participation yet"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {progressLoading ? (
              <div>Loading...</div>
            ) : progressData ? (
              <div className="space-y-6">
                {Object.entries(progressData.tasks).length > 0 ? (
                  <div className="grid grid-cols-3 gap-5">
                    {Object.entries(progressData.tasks).map(
                      ([taskId, task]) => {
                        // Find task name from questData if available
                        let taskName = `Task ${taskId.substring(0, 6)}...`;
                        if (questData) {
                          for (const week of questData.weeks) {
                            const foundTask = week.tasks.find(
                              (t) => t.id === taskId,
                            );
                            if (foundTask) {
                              taskName = foundTask.title;
                              break;
                            }
                          }
                        }

                        return (
                          <div
                            key={taskId}
                            className="border rounded-md p-4 aspect-square flex flex-col shadow-sm hover:shadow-md transition-shadow"
                          >
                            <div className="flex-1 flex flex-col">
                              <div className="flex items-start space-x-2 mb-2">
                                <div className="h-4 w-4 mt-0.5 bg-green-500 rounded-full flex-shrink-0" />
                                <p className="font-medium text-sm truncate">
                                  {taskName}
                                </p>
                              </div>

                              {task.imageUrl && (
                                <div className="mt-1 relative flex-1 min-h-[100px] overflow-hidden rounded-md">
                                  <img
                                    src={task.imageUrl}
                                    alt="Task completion"
                                    className="object-cover w-full h-full"
                                  />
                                </div>
                              )}

                              <div className="mt-auto pt-2 text-xs text-muted-foreground">
                                <p>
                                  Completed:{" "}
                                  {new Date(
                                    task.updatedAt,
                                  ).toLocaleDateString()}
                                </p>

                                {task.latitude && task.longitude && (
                                  <p className="mt-1">
                                    Location: {task.latitude.toFixed(4)},{" "}
                                    {task.longitude.toFixed(4)}
                                  </p>
                                )}
                                {task.distance !== undefined && (
                                  <p className="mt-1">
                                    Distance:{" "}
                                    {(task.distance * 1000).toFixed(2)}m
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                ) : (
                  <div>No completed tasks yet</div>
                )}
              </div>
            ) : (
              <div>No data available</div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
